import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useUserPerson } from "src/contexts/global/GlobalContext";
import {
  useIsWorkspaceLoading,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useOrgFeatureFlagsForTenant } from "src/hooks/orgConfig/hooks/useOrgFeatureFlagsForTenant";
import { inboxEnabled } from "src/inbox/inboxEnabled";

import {
  isIdleRoutePrefetchEnabledByParam,
  shouldSkipIdleRoutePrefetchForNetwork,
  startIdleRouteChunkPrefetch,
} from "./idleRouteChunkPrefetch.js";

/** @internal Hook tests inject a delayed importLoaders promise. */
export const idleRoutePrefetchDeps = {
  importLoaders: () => import("./getIdlePrefetchLoaders.js"),
};

/** After the shell is up, prefetch lazy route chunks during idle time. */
export function useIdleRouteChunkPrefetch(enabled = true) {
  const location = useLocation();
  const auth0 = useAuth0();
  const user = auth0?.user;
  const userSub = user?.sub;
  const userRef = useRef(user);
  userRef.current = user;
  const userPerson = useUserPerson();
  const userPersonAggregateId = userPerson?.aggregateId;
  const workspaceFlagsLoading = useIsWorkspaceLoading();
  const { featureFlags, loading: orgConfigLoading } =
    useOrgFeatureFlagsForTenant();
  const {
    disablePersonalisation,
    enableAIChat,
    enableAIDataDrivenDashboards,
    enableDemoODOptions,
    enableOrgDesign,
    enableTeamCanvas,
    enablePositionBackedVacancies,
    enableSlidesTeamByTeamPagination,
    enableWorkforceGuardrails,
    enableInbox,
  } = useWorkspaceFeatureFlags();

  const enableDesigns = Boolean(featureFlags?.designs?.enableDesigns);
  const inboxFlag = inboxEnabled({ featureFlags: { enableInbox } });

  const idleRoutePrefetchParam = useMemo(
    () => new URLSearchParams(location.search).get("idleRoutePrefetch"),
    [location.search]
  );

  const prefetchContext = useMemo(
    () => ({
      user: userSub ? userRef.current : undefined,
      userPersonAggregateId,
      disablePersonalisation: Boolean(disablePersonalisation),
      enableDesigns,
      orgConfigLoading,
      enableDemoODOptions: Boolean(enableDemoODOptions),
      workspaceFlagsLoading,
      enableAIChat: Boolean(enableAIChat),
      enableTeamCanvas: Boolean(enableTeamCanvas),
      enableOrgDesign: Boolean(enableOrgDesign),
      enablePositionBackedVacancies: Boolean(enablePositionBackedVacancies),
      enableAIDataDrivenDashboards: Boolean(enableAIDataDrivenDashboards),
      enableSlides: Boolean(enableSlidesTeamByTeamPagination),
      enableWorkforceGuardrails: Boolean(enableWorkforceGuardrails),
      enableInbox: inboxFlag,
    }),
    [
      userSub,
      userPersonAggregateId,
      disablePersonalisation,
      enableDesigns,
      orgConfigLoading,
      enableDemoODOptions,
      workspaceFlagsLoading,
      enableAIChat,
      enableTeamCanvas,
      enableOrgDesign,
      enablePositionBackedVacancies,
      enableAIDataDrivenDashboards,
      enableSlidesTeamByTeamPagination,
      enableWorkforceGuardrails,
      inboxFlag,
    ]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    if (!isIdleRoutePrefetchEnabledByParam(idleRoutePrefetchParam)) {
      return undefined;
    }
    if (shouldSkipIdleRoutePrefetchForNetwork()) return undefined;
    if (workspaceFlagsLoading || orgConfigLoading) return undefined;

    let cancelled = false;
    let cancelPrefetch = () => {};

    void idleRoutePrefetchDeps
      .importLoaders()
      .then(({ getIdlePrefetchLoaders }) => {
        if (cancelled) return;
        const loaders = getIdlePrefetchLoaders(prefetchContext);
        cancelPrefetch = startIdleRouteChunkPrefetch({ loaders });
      });

    return () => {
      cancelled = true;
      cancelPrefetch();
    };
  }, [
    enabled,
    idleRoutePrefetchParam,
    orgConfigLoading,
    prefetchContext,
    workspaceFlagsLoading,
  ]);
}
